/**
 * Resources Page
 *
 * The org's reference page: sections of links, files and notes. Mounted in both the
 * admin and crew layouts, like ProfilePage. Every member reads it; owners and
 * admins also get the editing controls (the API enforces the same split).
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { arrayMove } from '@dnd-kit/sortable';
import { useOrganization } from '../../context/OrganizationContext';
import { useResources } from '../../hooks/queries';
import {
  deleteResourceSection,
  reorderResourceSections,
  type ResourceSection,
} from '../../api/resources';
import { getErrorMessage } from '../../api/client';
import { ConfirmDialog, EmptyState } from '../../components/ui';
import ResourceSectionCard from '../../components/resources/ResourceSectionCard';
import ResourceSectionFormModal from '../../components/resources/ResourceSectionFormModal';
import { SortableItem, SortableList } from '../../components/resources/Sortable';

export default function ResourcesPage() {
  const { isOrgAdmin } = useOrganization();
  const { sections, canUploadFiles, loading, error, setSections, invalidate } = useResources();

  // null: closed. 'new': adding. A section: editing it.
  const [editing, setEditing] = useState<ResourceSection | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ResourceSection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMove = async (oldIndex: number, newIndex: number) => {
    const reordered = arrayMove(sections, oldIndex, newIndex);
    setSections(reordered);
    try {
      await reorderResourceSections(reordered.map((section) => section.id));
    } catch (err) {
      toast.error(getErrorMessage(err));
      invalidate();
    }
  };

  const handleSaved = () => {
    setEditing(null);
    invalidate();
    toast.success('Section saved');
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteResourceSection(deleting.id);
      setDeleting(null);
      invalidate();
      toast.success('Section deleted');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const itemCount = deleting?.items.length ?? 0;

  return (
    <div className="max-w-3xl">
      <div className="mb-6 pb-4 border-b border-hairline flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-heading text-ink">Resources</h1>
          <p className="text-ink-body text-sm mt-1">
            {isOrgAdmin
              ? 'Links, files and notes for your whole organization. Only owners and admins can edit them.'
              : 'Links, files and notes from your organization.'}
          </p>
        </div>
        {isOrgAdmin && sections.length > 0 && (
          <button onClick={() => setEditing('new')} className="btn-primary shrink-0">
            Add section
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-white border border-hairline border-l-[3px] border-l-program text-program-dark text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4" aria-label="Loading resources">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white border border-hairline p-4 animate-pulse">
              <div className="h-4 bg-gray-200 w-1/3 mb-3"></div>
              <div className="h-3 bg-gray-100 w-2/3"></div>
            </div>
          ))}
        </div>
      ) : sections.length === 0 ? (
        !error && (
          <div className="bg-white border border-hairline">
            <EmptyState
              title="No resources yet"
              message={
                isOrgAdmin
                  ? 'Add the links, files and notes your crew keeps asking for.'
                  : 'When your organization adds links, files and notes, they will show up here.'
              }
              action={
                isOrgAdmin && (
                  <button onClick={() => setEditing('new')} className="btn-primary">
                    Add section
                  </button>
                )
              }
            />
          </div>
        )
      ) : isOrgAdmin ? (
        <SortableList ids={sections.map((section) => section.id)} onMove={handleMove}>
          <div className="space-y-4">
            {sections.map((section) => (
              <SortableItem key={section.id} id={section.id} handleLabel={`Reorder ${section.title}`}>
                {(handle) => (
                  <ResourceSectionCard
                    section={section}
                    dragHandle={handle}
                    actions={
                      <div className="flex items-center gap-3 shrink-0 text-sm font-medium">
                        <button
                          onClick={() => setEditing(section)}
                          aria-label={`Edit ${section.title}`}
                          className="text-ink-body hover:text-ink"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleting(section)}
                          aria-label={`Delete ${section.title}`}
                          className="text-ink-body hover:text-program-dark"
                        >
                          Delete
                        </button>
                      </div>
                    }
                  />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableList>
      ) : (
        <div className="space-y-4">
          {sections.map((section) => (
            <ResourceSectionCard key={section.id} section={section} />
          ))}
        </div>
      )}

      {editing && (
        <ResourceSectionFormModal
          section={editing === 'new' ? null : editing}
          canUploadFiles={canUploadFiles}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete section"
        message={`Delete "${deleting?.title}" and its ${itemCount} ${
          itemCount === 1 ? 'item' : 'items'
        }? Your crew will no longer see them, and uploaded files are removed for good.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
}
